import React, { useMemo, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Box,
  Alert
} from '@cloudscape-design/components';
import { FormSchema, FormField as FormFieldType } from '../../../types/formSchema';
import { FieldRegistry } from './FieldRegistry';
import { FormThemeProvider } from './FormThemeProvider';
import { useFormStore } from '../../../store/form-stores';

/**
 * Configuration for custom action buttons in the form
 */
interface ActionButton {
  /** Button text content */
  text: string;
  /** Button visual style */
  variant: 'primary' | 'normal' | 'link';
  /** Position in form layout */
  position: 'left' | 'right';
  /** Function called when button is clicked */
  handler: (formData: any) => void;
  /** If true, button will bypass form validation and call handler directly */
  bypassValidation?: boolean;
}

/**
 * Props for the generic ConfigurableForm component
 * This form can be reused across different workflows (MFR, NetPPM, RVM, etc.)
 */
interface ConfigurableFormProps {
  /** Unique identifier for the workflow (used for state management) */
  workflowId: string;
  /** Form schema defining structure and validation */
  schema: FormSchema;
  /** Initial form values */
  initialValues?: any;
  /** Callback triggered on form submission */
  onSubmit?: (data: any) => void;
  /** Callback triggered when any field changes - handle workflow-specific cascading logic here */
  onFieldChange?: (fieldName: string, value: any) => void;
  /** Dynamic dropdown options (for cascading dropdowns) - managed by parent component */
  dynamicOptions?: Record<string, any[]>;
  /** Whether to show the default submit button */
  showSubmitButton?: boolean;
  /** Text for the default submit button */
  submitButtonText?: string;
  /** Whether to show the default reset button */
  showResetButton?: boolean;
  /** Whether to show the form title header */
  showHeader?: boolean;
  /** Custom action buttons with positioning and validation behavior */
  actionButtons?: ActionButton[];
  /** Additional CSS class name */
  className?: string;
}

/**
 * Generic Configurable Form Component
 * 
 * A reusable form component that can be used across different workflows (MFR, NetPPM, RVM, etc.)
 * 
 * Features:
 * - Schema-driven form generation with Ant Design input components
 * - Cloudscape layout and typography for consistency
 * - Real-time validation with React Hook Form
 * - Persistent form state (localStorage)
 * - Dynamic dropdown options with cascading logic support
 * - Flexible action button positioning (left = cancel/destructive, right = save/primary)
 * - Professional UI with borders, separators, and proper layout
 * - Theme integration for Ant Design components
 * 
 * Usage Pattern:
 * 1. Define form schema with fields and validation rules
 * 2. Handle workflow-specific cascading logic in parent's onFieldChange
 * 3. Pass dynamic options for cascading dropdowns via dynamicOptions prop
 * 4. Configure action buttons with proper positioning
 * 
 * @example
 * ```tsx
 * <ConfigurableForm
 *   workflowId="mfr-create"
 *   schema={mfrFormSchema}
 *   onFieldChange={handleMFRFieldChange}
 *   dynamicOptions={cascadingOptions}
 *   actionButtons={[
 *     { text: 'Cancel', variant: 'link', position: 'left', handler: handleCancel },
 *     { text: 'Save', variant: 'normal', position: 'right', handler: handleSave },
 *     { text: 'Save & Run', variant: 'primary', position: 'right', handler: handleSaveAndRun }
 *   ]}
 * />
 * ```
 */
export const ConfigurableForm: React.FC<ConfigurableFormProps> = ({
  workflowId,
  schema,
  initialValues,
  onSubmit,
  onFieldChange,
  dynamicOptions = {},
  showSubmitButton = true,
  submitButtonText = 'Submit',
  showResetButton = true,
  showHeader = true,
  actionButtons,
  className
}) => {
  const { 
    getFormData, 
    setFormData, 
    updateFormField,
    setLoading,
    isLoading
  } = useFormStore();

  // Get default values from schema and combine with stored data and initial values
  const getDefaultValues = () => {
    const schemaDefaults: any = {};
    
    schema.sections.forEach(section => {
      section.fields.forEach(field => {
        if (field.type === 'documentSectionsTable') {
          // Initialize document sections with default checkbox states
          const tableField = field as any;
          const tableDefaults: any = {};
          
          tableField.tableData?.forEach((item: any) => {
            if (!item.isParent) {
              tableDefaults[item.id] = {
                includeTable: item.includeTable,
                includeCommentary: item.includeCommentary,
                filterValue: null
              };
            }
          });
          
          schemaDefaults[field.name] = tableDefaults;
        } else if (field.defaultValue !== undefined) {
          schemaDefaults[field.name] = field.defaultValue;
        }
      });
    });

    const storedData = getFormData(workflowId);
    const finalDefaults = { ...schemaDefaults, ...storedData, ...initialValues };
    
    // Save defaults to store if not already present or if stored data is empty
    if (Object.keys(storedData).length === 0) {
      setFormData(workflowId, finalDefaults);
    }
    
    return finalDefaults;
  };

  // Initialize form with React Hook Form
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    trigger,
    getValues,
    setFocus,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: getDefaultValues(),
    mode: 'onChange', // Real-time validation
    shouldFocusError: true
  });

  // Watch all form values
  const watchedValues = watch();

  // Memoize all fields for efficiency
  const allFields = useMemo(() => {
    return schema.sections.flatMap(section => section.fields);
  }, [schema]);

  // Generic field change handler - no workflow-specific logic
  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    updateFormField(workflowId, fieldName, value);
    onFieldChange?.(fieldName, value);
  }, [workflowId, updateFormField, onFieldChange]);

  // Handle form submission
  const handleFormSubmit = async (data: any) => {
    setLoading(workflowId, true);
    
    try {
      // Save final form data
      setFormData(workflowId, data);
      
      // Call onSubmit callback
      if (onSubmit) {
        await onSubmit(data);
      } else {
        // Default behavior - just log the data
        console.log('Form submitted:', { workflowId, data });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setLoading(workflowId, false);
    }
  };

  // Handle button click with validation
  const handleButtonClick = useCallback(async (handler: (data: any) => void) => {
    // Trigger validation and get form data
    const isValid = await trigger();
    
    if (isValid) {
      const formData = getValues();
      await handler(formData);
    } else {
      // Scroll to section containing the first error field
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        // Find which section contains the error field
        const errorSection = schema.sections.find(section => 
          section.fields.some(field => field.name === firstErrorField)
        );
        
      }
    }
  }, [trigger, getValues, errors, schema.sections]);

  // Handle form reset
  const handleReset = () => {
    const defaultValues = getDefaultValues();
    reset(defaultValues);
    setFormData(workflowId, defaultValues);
  };

  // Get dynamic options for field rendering
  const getDynamicOptions = (): Record<string, any[]> => {
    // Return the component's dynamic options state
    return dynamicOptions;
  };

  // Check if field should be visible
  const isFieldVisible = (field: FormFieldType): boolean => {
    if (!field.visibleWhen) return true;
    
    const { field: dependentField, value: expectedValue } = field.visibleWhen;
    const currentValue = watchedValues[dependentField];
    
    return currentValue === expectedValue;
  };

  // Render a single field
  const renderField = (field: FormFieldType) => {
    if (!isFieldVisible(field)) return null;

    const FieldComponent = FieldRegistry[field.type as keyof typeof FieldRegistry];
    
    if (!FieldComponent) {
      console.warn(`Unknown field type: ${field.type}`);
      return (
        <Alert type="warning" key={field.name}>
          Unknown field type: {field.type}
        </Alert>
      );
    }

    return (
      <FieldComponent
        key={field.name}
        field={field}
        control={control}
        dynamicOptions={getDynamicOptions()}
        onFieldChange={handleFieldChange}
      />
    );
  };

  // Render form sections with layout support
  const renderSections = () => {
    return schema.sections.map((section, sectionIndex) => (
      <Box key={sectionIndex}>
        {section.title && (
          <Header variant="h2" description={section.description}>
            {section.title}
          </Header>
        )}
        <Box margin={{ top: section.title ? "m" : undefined }}>
          {section.layout === 'two-column' ? (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
              gap: '1rem',
              alignItems: 'start'
            }}>
              {section.fields.map(renderField)}
            </div>
          ) : (
            <SpaceBetween size="l">
              {section.fields.map(renderField)}
            </SpaceBetween>
          )}
        </Box>
      </Box>
    ));
  };

  const loading = isLoading(workflowId);

  return (
    <FormThemeProvider>
      <div className={className}>
        <Container>
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <SpaceBetween size="l">
              {/* Optional form header */}
              {showHeader && (
                <Header variant="h1" description={schema.description}>
                  {schema.title}
                </Header>
              )}

              {/* Form sections */}
              {renderSections()}

              {/* Horizontal separator above actions */}
              {(actionButtons?.length || showSubmitButton || showResetButton) && (
                <>
                  <hr style={{ 
                    border: 'none', 
                    borderTop: '1px solid #e9ecef', 
                    margin: 0 
                  }} />
                  
                  <Box padding="l">
                    {actionButtons?.length ? (
                      // Custom action buttons layout - use flexbox for proper left/right positioning
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        width: '100%'
                      }}>
                        {/* Left side buttons */}
                        <div>
                          <SpaceBetween direction="horizontal" size="xs">
                            {actionButtons
                              .filter(btn => btn.position === 'left')
                              .map((btn, idx) => (
                                <Button
                                  key={idx}
                                  variant={btn.variant}
                                  disabled={loading || isSubmitting}
                                  onClick={() => {
                                    if (btn.bypassValidation) {
                                      // Bypass validation - call handler directly
                                      btn.handler(watchedValues);
                                    } else {
                                      // Go through validation first
                                      handleButtonClick(btn.handler);
                                    }
                                  }}
                                >
                                  {btn.text}
                                </Button>
                              ))}
                          </SpaceBetween>
                        </div>
                        
                        {/* Right side buttons */}
                        <div>
                          <SpaceBetween direction="horizontal" size="xs">
                            {actionButtons
                              .filter(btn => btn.position === 'right')
                              .map((btn, idx) => (
                                <Button
                                  key={idx}
                                  variant={btn.variant}
                                  disabled={loading || isSubmitting}
                                  loading={btn.variant === 'primary' && (loading || isSubmitting)}
                                  onClick={() => {
                                    if (btn.bypassValidation) {
                                      // Bypass validation - call handler directly
                                      btn.handler(watchedValues);
                                    } else {
                                      // Go through validation first - all buttons use same path
                                      handleButtonClick(btn.handler);
                                    }
                                  }}
                                >
                                  {btn.text}
                                </Button>
                              ))}
                          </SpaceBetween>
                        </div>
                      </div>
                    ) : (
                      // Default buttons - right aligned
                      <Box textAlign="right">
                        <SpaceBetween direction="horizontal" size="xs">
                          {showResetButton && (
                            <Button
                              variant="normal"
                              disabled={loading || isSubmitting}
                              onClick={handleReset}
                            >
                              Reset
                            </Button>
                          )}
                          
                          {showSubmitButton && (
                            <Button
                              variant="primary"
                              disabled={loading || isSubmitting}
                              loading={loading || isSubmitting}
                              formAction="submit"
                            >
                              {submitButtonText}
                            </Button>
                          )}
                        </SpaceBetween>
                      </Box>
                    )}
                  </Box>
                </>
              )}

              {/* Debug info in development */}
              {(typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') && (
                <Box>
                  <Header variant="h3">Debug Info</Header>
                  <Box>
                    <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                      {JSON.stringify({ 
                        workflowId,
                        formData: watchedValues,
                        errors,
                        dynamicOptions: getDynamicOptions()
                      }, null, 2)}
                    </pre>
                  </Box>
                </Box>
              )}
            </SpaceBetween>
          </form>
        </Container>
      </div>
    </FormThemeProvider>
  );
};
